import { useBookingStore, BookingState } from '@/stores/booking';
import { useStateStore, MODE } from '@/stores/state';
import { useAvailabilityStore } from '@/stores/booking-availability';
import { useErrorStore } from '@/stores/error';
import { LocationsRepo } from '@/repos/locations';
import { ConsultationRepo } from '@/repos/consultation';

import { formatTimeToHHMM } from '@/helpers/dateFormats';
import { handleAPIErrors } from '@/helpers/errorHandler';
import { userDataSchema, phoneSchema } from '~/schemas/booking';
import { ZodIssue } from 'zod';
import { ILocation } from './gymClusters';

export const getAllLocations = async (): Promise<ILocation[]> => {
    const repo = new LocationsRepo(useRuntimeConfig());
    const locations = await repo.get('/all');

    return locations.data.locations;
};

/**
 * Asynchronously refreshes availability by updating and navigating based on the result.
 *
 * @return {Promise<void>} Promise that resolves once availability is refreshed.
 */
export const refreshAvailability = async () => {
    const bookingStore = useBookingStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();
    try {
        await updateAvailability(
            bookingStore,
            availabilityStore,
            errorStore,
            runtimeConfig
        );
        navigateTo('/date');
    } catch (error) {
        navigateTo('/error');
    }
};

/**
 * Asynchronously loads an enquiry based on the provided enquiry ID, updating availability and returning true if successful.
 *
 * @param {string | any} enquiry_id - The ID of the enquiry to load.
 * @return {Promise<boolean>} A promise that resolves to true if the enquiry is successfully loaded, false otherwise.
 */
export const loadEnquiry = async (
    enquiry_id: string | any = false,
    path: string = ''
): Promise<boolean> => {
    const bookingStore = useBookingStore();
    const stateStore = useStateStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    if (!stateStore || stateStore.isFirstLoad()) return false;

    stateStore.setMode(MODE.NEW);

    if (!bookingStore) return false;

    try {
        const saved_details = bookingStore.restoreFromLocalStorage();
        enquiry_id = stateStore.getRequestId(enquiry_id);

        if (saved_details) {
            const routeMaxAdvance: Record<string, string> = {
                '/date-time': 'P14D',
                '/book-now': 'P10D',
            };

            const maxAdvance = routeMaxAdvance[path] ?? 'P40D';

            bookingStore.updateBookingState({
                max_advance: maxAdvance,
            });

            await updateAvailability(
                bookingStore,
                availabilityStore,
                errorStore,
                runtimeConfig
            );
            stateStore.setFirstLoadFalse();
            return true;
        } else if (enquiry_id) {
            await fetchAndStoreEnquiry(
                enquiry_id,
                bookingStore,
                stateStore,
                errorStore,
                runtimeConfig
            );
            await fetchAndStoreAvailability(
                bookingStore,
                availabilityStore,
                errorStore,
                runtimeConfig,
                bookingStore.state.location
            );
            stateStore.setFirstLoadFalse();
            bookingStore.regionBasedConsent();
            return true;
        }
    } catch (error) {
        console.error('loadEnquiry', error);
    }
    return false;
};

/**
 * Loads a booking based on the provided booking and business IDs.
 *
 * @param {boolean | string} booking_id - The ID of the booking.
 * @param {false | string} business_id - The ID of the business.
 * @return {Promise<boolean>} A promise that resolves to true if the booking is successfully loaded, false otherwise.
 */
export const loadBooking = async (
    booking_id: boolean | string = false,
    business_id: false | string = false
): Promise<boolean> => {
    const bookingStore = useBookingStore();
    const stateStore = useStateStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();
    if (!stateStore) {
        throw new Error('Load Booking Function: stateStore is null');
    }
    if (stateStore.isFirstLoad()) return false;
    if (!bookingStore) {
        throw new Error('Load Booking Function: bookingStore is null');
    }

    const saved_details = bookingStore.restoreFromLocalStorage();
    const request_id = stateStore.getRequestId(booking_id);
    business_id = stateStore.getBusiness(business_id);

    if (!request_id) {
        throw new Error('Load Booking Function: request_id is null');
    }

    if (!business_id) {
        throw new Error('Load Booking Function: business_id is null');
    }

    stateStore.setMode(MODE.EXISTING);

    try {
        if (saved_details) {
            await updateAvailability(
                bookingStore,
                availabilityStore,
                errorStore,
                runtimeConfig
            );
            stateStore.setFirstLoadFalse();
            return true;
        } else {
            await fetchAndStoreBooking(
                request_id,
                business_id,
                bookingStore,
                stateStore,
                errorStore,
                runtimeConfig
            );

            await fetchAndStoreAvailability(
                bookingStore,
                availabilityStore,
                errorStore,
                runtimeConfig,
                bookingStore.state.location
            );
            stateStore.setFirstLoadFalse();
            return true;
        }
    } catch (error) {
        return false;
    }
};

/**
 * Asynchronously restores the state and availability, handling exceptions related to local storage and fetching availability.
 *
 * @return {Promise<boolean>} Returns a boolean indicating the success of the restoration process.
 */
export const restoreStateAndAvailability = async (): Promise<boolean> => {
    const bookingStore = useBookingStore();
    const stateStore = useStateStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    if (!bookingStore.restoreFromLocalStorage()) return false;

    const locationId = stateStore.getLocationId();
    if (!locationId) return false;

    stateStore.setNoEnquiry();
    stateStore.setMode(MODE.NEW);
    stateStore.setFirstLoadFalse();
    stateStore.setPageLoadedTrue();

    if (availabilityStore.isEmpty()) {
        try {
            await fetchAndStoreAvailability(
                bookingStore,
                availabilityStore,
                errorStore,
                runtimeConfig,
                locationId
            );
            bookingStore.regionBasedConsent();
            return true;
        } catch (error) {
            console.error('Unable to restore state availability', error);
            return false;
        }
    }
    return false;
};

/**
 * Asynchronously requests availability for a given location ID.
 *
 * @param {string} locationId - The ID of the location for which to request availability.
 * @return {Promise<string | false>} A promise that resolves to the location ID if availability is successfully requested, or false if an error occurs.
 */
export const requestAvailability = async (locationId: string) => {
    const bookingStore = useBookingStore();
    const stateStore = useStateStore();
    const availabilityStore = useAvailabilityStore();
    const errorStore = useErrorStore();
    const runtimeConfig = useRuntimeConfig();

    if (!locationId) {
        console.error('Request Availability: locationId is invalid');
        return false;
    }

    try {
        await fetchAndStoreAvailability(
            bookingStore,
            availabilityStore,
            errorStore,
            runtimeConfig,
            locationId
        );
        bookingStore.regionBasedConsent();
        stateStore.setNoEnquiry();
        stateStore.setMode(MODE.NEW);
        stateStore.getLocationId(locationId);
        stateStore.setFirstLoadFalse();
        stateStore.setPageLoadedTrue();
        return locationId;
    } catch (error) {
        return false;
    }
};

export const createBookingPayload = (bookingState: BookingState) => {
    const payload: BookingState = {
        ...bookingState,
        start_date: bookingState.selected_date,
        end_date: bookingState.selected_date,
        start_time: formatTimeToHHMM(bookingState.start_time),
        end_time: formatTimeToHHMM(bookingState.end_time),
    };
    return payload;
};

// API interaction functions
export const getEnquiry = async (
    enquiry_id: string,
    bookingStore: ReturnType<typeof useBookingStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const enquiryData = await repo.getEnquiry(enquiry_id);
    bookingStore.updateBookingState(enquiryData.data);
};

export const getBooking = async (
    booking_id: string,
    business_id: string,
    bookingStore: ReturnType<typeof useBookingStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const bookingData = await repo.getBooking(booking_id, business_id);
    bookingStore.updateBookingState(bookingData.data);
};

export const getAvailability = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    availabilityStore: ReturnType<typeof useAvailabilityStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const availability = await repo.getAvailability(bookingStore.state);
    availabilityStore.setAvailability(availability.data);
};

export const getBusiness = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const businessData = await repo.getBusiness(bookingStore.state.location);
    bookingStore.updateBookingState(businessData.data);
};

export const getPartialBusinessInfo = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const businessData = await repo.getBusiness(bookingStore.state.location);
    const { booking_business, service_id, ...data } = businessData.data;

    bookingStore.updateBookingState(data);
};

export const getNonCachedBookingService = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const response = await repo.getNonCachedService(
        bookingStore.state.booking_business,
        bookingStore.state.service_id ?? ''
    );
    bookingStore.updateBookingState(response.data);
};

export const updateAvailability = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    availabilityStore: ReturnType<typeof useAvailabilityStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any
) => {
    errorStore.clearErrors();

    try {
        await getAvailability(bookingStore, availabilityStore, runtimeConfig);
    } catch (error) {
        handleAPIErrors('AVAILABILITY', error);
    }
};

export const fetchAndStoreAvailability = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    availabilityStore: ReturnType<typeof useAvailabilityStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any,
    location_id?: string,
    is10days: boolean = false
) => {
    errorStore.clearErrors();
    if (location_id) {
        bookingStore.updateBookingState({ location: location_id });
    }

    try {
        if (bookingStore.isExistingBooking()) {
            await getPartialBusinessInfo(bookingStore, runtimeConfig);
            await getNonCachedBookingService(bookingStore, runtimeConfig);
        } else if (is10days) {
            await getBusiness(bookingStore, runtimeConfig);
            bookingStore.updateBookingState({
                max_advance: 'P10D', // 10 days advance for availability, used for location selection step
            });
        } else {
            await getBusiness(bookingStore, runtimeConfig);
        }

        if (bookingStore.isInternal()) {
            bookingStore.updateBookingState({
                min_lead_time: 'PT0M', // immediate
                max_advance: 'P40D', // 40 days (roughly 1.3 months)
            });
        }
        await getAvailability(bookingStore, availabilityStore, runtimeConfig);
    } catch (error) {
        if (is10days) {
            // hide old UI error msg at the bottom
            console.error(error);
        } else {
            errorStore.setError(error);
            handleAPIErrors('BUSINESS', error);
        }
    }
};

export const isBookingValid = (
    bookingState: BookingState
): { valid: boolean; issues: ZodIssue[] } => {
    const userDataValidationResult = userDataSchema.safeParse(bookingState);
    const phoneValidationResult = phoneSchema.safeParse(bookingState);

    const issues: ZodIssue[] = [];

    if (!userDataValidationResult.success) {
        issues.push(...userDataValidationResult.error.errors);
    }

    if (!phoneValidationResult.success) {
        issues.push(...phoneValidationResult.error.errors);
    }

    return {
        valid: issues.length === 0,
        issues,
    };
};

export const fetchAndStoreEnquiry = async (
    enquiry_id: string,
    bookingStore: ReturnType<typeof useBookingStore>,
    stateStore: ReturnType<typeof useStateStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any
) => {
    errorStore.clearErrors();

    try {
        await getEnquiry(enquiry_id, bookingStore, runtimeConfig);
    } catch (error) {
        stateStore.setRequestId(false);
        handleAPIErrors('REQUEST_ID', error);
    }
};

export const fetchAndStoreBooking = async (
    booking_id: string,
    business_id: string,
    bookingStore: ReturnType<typeof useBookingStore>,
    stateStore: ReturnType<typeof useStateStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any
) => {
    errorStore.clearErrors();

    try {
        await getBooking(booking_id, business_id, bookingStore, runtimeConfig);
        if (booking_id) {
            bookingStore.updateBookingState({
                booking_id: booking_id,
                booking_business: business_id,
            });
        }
    } catch (error) {
        stateStore.setRequestId(false);
        stateStore.setBusiness(false);
        handleAPIErrors('REQUEST_ID', error);
    }
};

export const saveConsultation = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    stateStore: ReturnType<typeof useStateStore>,
    availabilityStore: ReturnType<typeof useAvailabilityStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any,
    mode: string
) => {
    errorStore.clearErrors();
    const booking = bookingStore.state;

    try {
        const available = await checkIfTimeSlotIsAvailable(
            booking.selected_date,
            booking.start_time,
            booking.end_time,
            bookingStore,
            availabilityStore,
            errorStore,
            runtimeConfig
        );

        if (!available) {
            errorStore.setErrorWithRefresh();
            return;
        }

        const payload = createBookingPayload(booking);
        const repo = new ConsultationRepo(runtimeConfig);

        const response =
            mode === MODE.EXISTING && payload?.booking_id !== undefined && payload?.booking_id !== ''
                ? await repo.updateBooking(payload)
                : await repo.createBooking(payload);

        if (response.data?.isDuplicate) {
            bookingStore.updateBookingState({ is_duplicate: response.data.isDuplicate })
            return;
        }
        stateStore.getRequestId(response.data.appointment_id);
        stateStore.getBusiness(booking.booking_business);

        //Clearing localStorage and sessionStorage to avoid any stale data
        localStorage.clear();
    } catch (error) {
        throw error;
    }
};

export const virtualConsultation = async (
    bookingStore: ReturnType<typeof useBookingStore>,
    stateStore: ReturnType<typeof useStateStore>,
    runtimeConfig: any
) => {
    const repo = new ConsultationRepo(runtimeConfig);
    const bookingState = {
        ...bookingStore.state,
        is_online: true,
        selected_date: bookingStore.state.start_date,
        selected_time: bookingStore.state.start_time,
    };

    bookingStore.updateBookingState(bookingState);

    try {
        const response = await repo.updateBooking(
            createBookingPayload(bookingState)
        );
        stateStore.getRequestId(response.data.appointment_id);    //TO DO
        stateStore.getBusiness(bookingStore.state.booking_business);

        //Clearing localStorage and sessionStorage to avoid any stale data
        localStorage.clear();
    } catch (error) {
        handleAPIErrors('UPDATE', error);
    }
};

export const cancelConsultationBooking = async (
    bookingState: ReturnType<typeof useBookingStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any
) => {
    errorStore.clearErrors();

    const repo = new ConsultationRepo(runtimeConfig);
    try {
        await repo.cancelBooking(createBookingPayload(bookingState.state));

        //Clearing localStorage and sessionStorage to avoid any stale data
        localStorage.clear();
    } catch (error) {
        handleAPIErrors('CANCEL', error);
    }
};

const checkIfTimeSlotIsAvailable = async (
    requestedDate: string,
    requestedStartTime: string,
    requestedEndTime: string,
    bookingStore: ReturnType<typeof useBookingStore>,
    availabilityStore: ReturnType<typeof useAvailabilityStore>,
    errorStore: ReturnType<typeof useErrorStore>,
    runtimeConfig: any
) => {
    try {
        // Re-request availability data
        await getAvailability(bookingStore, availabilityStore, runtimeConfig);

        const dayAvailability = availabilityStore.state[requestedDate];

        if (!dayAvailability) {
            return false;
        }
        const availableStaffIds = returnStaffForTimeSlot(
            requestedStartTime,
            requestedEndTime,
            dayAvailability
        );
        if (availableStaffIds.length > 0) {
            bookingStore.updateBookingState({
                staff_ids: availableStaffIds,
                staff_id:
                    availableStaffIds[
                        Math.floor(Math.random() * availableStaffIds.length)
                    ],
            });
        } else {
            return false;
        }
    } catch (error) {
        errorStore.setError(error);
        return false;
    }
    return true;
};

const returnStaffForTimeSlot = (
    requestedStartTime: string,
    requestedEndTime: string,
    dayRequested: any
) => {
    const slot = dayRequested.find(
        (search: any) =>
            search.start === requestedStartTime &&
            search.end === requestedEndTime
    );
    return slot ? slot.staff : [];
};
